import React from "react";
import { graphql } from "gatsby";
import { Helmet } from "react-helmet";

import Layout from "../../components/Layout";
import AvailableAssets from "../../components/AvailableAssetsTable";

import Hero from "../../components/Hero";
import EEnjoyZipcrewCard from "../../components/EEnjoyZipcrewCard";
import EEarnInFiveMins from "../../components/EEarnInFiveMins";
import GuildLine from "../../components/GuildLine";
import TrustedMediaList from "../../components/TrustedMediaList";
import FeaturedIconList from "../../components/FeaturedIconList";
import SideContentTemplate from "../../components/ContentComponents/SideContentTemplate";
import PartialContentTemplate from "../../components/ContentComponents/PartialContentTemplate";
import PartialPointList from "../../components/PartialPointList";
import GetOnBoard from "../../components/ContentComponents/GetOnBoard";
import Faq, { convertSupportCentreToFaqs } from "../../components/Faq";
import WithI18next from "../../components/WithI18next";
import { getAcfPartialContent, formatAcfPartial } from "../../utils";

const EarnPage = ({ data, t, lang }) => {
  return (
    <Layout title={t("earn.seo.title")} description={t("earn.seo.description")}>
      <Helmet>
        <body className="earn" />
      </Helmet>

      <Hero
        classNames="earn-page"
        backgroundColor="blue"
        content={data.heroContent}
        iconGooglePlayStore={data.iconGooglePlayStore}
        iconAppleAppStore={data.iconAppleAppStore}
        productIcon={data.zipupIcon}
        productName={t("menu.earn.zipup.title")}
      />

      <FeaturedIconList icons={data.featuredIconsContent.edges} />

      <AvailableAssets
        content={data.availableAssetsContent}
        coinInterestRates={data.coinInterestRates}
        productName={"earn"}
      />

      <TrustedMediaList links={data.mediaLinks} />

      {data.buyLockUpZmtsContent?.edges?.length > 0 && (
        <PartialContentTemplate
          headerHTML={`${t(
            "earn.buyLockupZmts.title"
          )} <br class="desktop" />ZipCrew!`}
          isPartialCentered={true}
        >
          <EEnjoyZipcrewCard content={data.buyLockUpZmtsContent} />
        </PartialContentTemplate>
      )}

      {data.zipupAccountContent?.edges?.length > 0 && (
        <SideContentTemplate
          {...formatAcfPartial(data.zipupAccountContent)}
          imgURL={data.imageZipUp.publicURL}
          imgMaxWidth={450}
          isImgHiddenOnMobile={false}
          backgroundImg={"primary"}
          isContentReversed={true}
        />
      )}

      <GuildLine
        title={t("earn.earnInterest.title")}
        imgs={[
          data.guildLineImageStep2.publicURL,
          data.guildLineImageStep3.publicURL,
        ]}
        steps={t("earn.earnInterest.steps", { returnObjects: true })}
      />

      {data.earnInFiveMinsContent?.edges?.length > 0 && (
        <EEarnInFiveMins content={data.earnInFiveMinsContent} />
      )}

      {data.whySaveWithZipmexContent?.edges?.length > 0 && (
        <PartialContentTemplate
          headerHTML={t("earn.whySaveWithZipmex.title")}
          imgURL={
            data.whySaveThImg?.publicURL ||
            data.whySaveIdImg?.publicURL ||
            data.whySaveGlobalImg?.publicURL
          }
        >
          <PartialPointList points={data.whySaveWithZipmexContent.edges} />
        </PartialContentTemplate>
      )}

      <Faq content={convertSupportCentreToFaqs(data.faqContent)} />

      <GetOnBoard content={getAcfPartialContent(data.earnMoreContent)} />
    </Layout>
  );
};

export default WithI18next()(EarnPage);

export const query = graphql`
  query (
    $wpLocale: String!
    $wpGlobalConfig: String!
    $isGlobal: Boolean!
    $isThai: Boolean!
    $isIndo: Boolean!
  ) {
    ...MediaLinks
    ...EarnMoreContent

    zipupIcon: file(relativePath: { eq: "images/menus/icons/zipup.svg" }) {
      publicURL
    }

    heroContent: allWpHeroSectionItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfHeroSections: { hsPage: { eq: "earn" } }
      }
    ) {
      edges {
        node {
          acfHeroSections {
            hsMainHeading
            hsMainText
            hsButtonText
            hsButtonLink
          }
        }
      }
    }
    iconGooglePlayStore: file(relativePath: { eq: "images/icons/gplay.svg" }) {
      publicURL
    }
    iconAppleAppStore: file(
      relativePath: { eq: "images/icons/applestore.svg" }
    ) {
      publicURL
    }

    featuredIconsContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "featured-icons" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pIcon {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    }

    availableAssetsContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { in: [".all", $wpGlobalConfig] } }
        acfPartials: { pPartialId: { eq: "available-assets" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
          }
        }
      }
    }
    coinInterestRates: allWpCoinInterestRatesItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: ".all" } }
        language: { locale: { eq: "en_US" } }
      }
      sort: { order: ASC, fields: acfCoinInterestRates___cirOrder }
    ) {
      nodes {
        acfCoinInterestRates {
          cirCoinIcon {
            localFile {
              publicURL
            }
          }
          cirCoinIconTable {
            localFile {
              publicURL
            }
          }
          zipupInterateRateV2 {
            vip0 {
              rate
            }
            vip1 {
              rate
            }
            vip2 {
              rate
            }
            vip3 {
              rate
            }
            vip4 {
              rate
            }
          }
          cirCoinCode
          cirZiplockRate
        }
      }
    }

    # TODO: split this part on WP
    buyLockUpZmtsContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: ".com.com.au" } }
        acfPartials: { pPartialId: { eq: "buy-lock-up-zmts" } }
        language: { locale: { eq: "en_US" } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pElementId
            pHeading
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
            pButtonText
            pButtonUrl
          }
        }
      }
    }

    zipupAccountContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "zipup-account" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
          }
        }
      }
    }
    imageZipUp: file(relativePath: { eq: "images/zipup.svg" }) {
      publicURL
    }

    earnInFiveMinsContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "five-min-cta" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pButtonText
            pButtonUrl
          }
        }
      }
    }

    whySaveWithZipmexContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "why-save-with-zipmex" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    }

    whySaveThImg: file(relativePath: { eq: "images/why-save-zipmex-th.png" })
      @include(if: $isThai) {
      publicURL
    }
    whySaveIdImg: file(relativePath: { eq: "images/why-save-zipmex-id.png" })
      @include(if: $isIndo) {
      publicURL
    }
    whySaveGlobalImg: file(
      relativePath: { eq: "images/why-save-zipmex-global.png" }
    ) @include(if: $isGlobal) {
      publicURL
    }

    guildLineImageStep2: file(
      relativePath: { eq: "images/total-deposit.svg" }
    ) {
      publicURL
    }
    guildLineImageStep3: file(
      relativePath: { eq: "images/interest-account.png" }
    ) {
      publicURL
    }

    faqContent: allWpSupportCenterItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { in: [".all", $wpGlobalConfig] } }
        categories: { nodes: { elemMatch: { slug: { eq: "zipup" } } } }
      }
      sort: { order: DESC, fields: date }
    ) {
      edges {
        node {
          title
          content
        }
      }
    }
  }
`;
